import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import {
    User,
    onIdTokenChanged,
    sendSignInLinkToEmail,
    signInWithEmailAndPassword,
    signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase_config';

const EMAIL_LINK_PENDING_KEY = 'adminEmailLinkPending';
const EMAIL_LINK_STATE_PARAM = 'adminEmailLinkState';
const EMAIL_LINK_EXPIRES_IN_MS = 15 * 60 * 1000;
const VERIFIED_SESSION_EXPIRES_IN_MS = 8 * 60 * 60 * 1000;
const VERIFIED_AUTH_TIME_CLAIM = 'adminEmailLinkAuthTime';

const getFirebaseErrorCode = (error: unknown): string => {
    if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'string'
    ) {
        return error.code;
    }

    return 'unknown';
};

interface PendingEmailLink {
    email: string;
    uid: string;
    state: string;
    requestedAt: number;
}

interface CompleteEmailLinkRequest {
    apiKey: string;
    idToken: string;
    oobCode: string;
}

interface CompleteEmailLinkResponse {
    verified: boolean;
}

interface EmailLinkVerification {
    verified: boolean;
    expiresAt: number | null;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    emailLinkVerified: boolean;
    pendingEmail: string | null;
    signIn: (email: string, password: string) => Promise<string>;
    completeEmailLinkSignIn: (link: string) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const readPendingEmailLink = (): PendingEmailLink | null => {
    try {
        const value = localStorage.getItem(EMAIL_LINK_PENDING_KEY);
        const pending = value ? JSON.parse(value) as PendingEmailLink : null;

        if (!pending || Date.now() - pending.requestedAt > EMAIL_LINK_EXPIRES_IN_MS) {
            localStorage.removeItem(EMAIL_LINK_PENDING_KEY);
            return null;
        }
        return pending;
    } catch {
        localStorage.removeItem(EMAIL_LINK_PENDING_KEY);
        return null;
    }
};

const createState = (): string => {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
};

const getEmailLinkVerification = async (user: User): Promise<EmailLinkVerification> => {
    const tokenResult = await user.getIdTokenResult();
    const authTime = Number(tokenResult.claims.auth_time);
    const verifiedAuthTime = Number(tokenResult.claims[VERIFIED_AUTH_TIME_CLAIM]);
    const expiresAt = authTime * 1000 + VERIFIED_SESSION_EXPIRES_IN_MS;
    const verified = (
        Number.isFinite(authTime) &&
        authTime === verifiedAuthTime &&
        expiresAt > Date.now()
    );

    return {
        verified,
        expiresAt: verified ? expiresAt : null,
    };
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [emailLinkVerified, setEmailLinkVerified] = useState(false);
    const [pendingEmail, setPendingEmail] = useState<string | null>(
        () => readPendingEmailLink()?.email ?? null
    );
    const [verificationExpiresAt, setVerificationExpiresAt] = useState<number | null>(null);

    useEffect(() => {
        let active = true;
        const unsubscribe = onIdTokenChanged(auth, async (nextUser) => {
            if (!active) {
                return;
            }

            setUser(nextUser);

            try {
                if (nextUser) {
                    const verification = await getEmailLinkVerification(nextUser);
                    if (active) {
                        setEmailLinkVerified(verification.verified);
                        setVerificationExpiresAt(verification.expiresAt);
                    }
                } else {
                    localStorage.removeItem(EMAIL_LINK_PENDING_KEY);
                    setPendingEmail(null);
                    setEmailLinkVerified(false);
                    setVerificationExpiresAt(null);
                }
            } catch {
                setEmailLinkVerified(false);
                setVerificationExpiresAt(null);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        });

        return () => {
            active = false;
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!verificationExpiresAt) {
            return;
        }

        const remaining = verificationExpiresAt - Date.now();
        const expireSession = () => {
            setEmailLinkVerified(false);
            setVerificationExpiresAt(null);
            void signOut(auth);
        };

        if (remaining <= 0) {
            expireSession();
            return;
        }

        const timer = window.setTimeout(expireSession, remaining);
        return () => window.clearTimeout(timer);
    }, [verificationExpiresAt]);

    const signIn = useCallback(async (email: string, password: string) => {
        setEmailLinkVerified(false);
        setVerificationExpiresAt(null);

        let credential;
        try {
            credential = await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            throw new Error(
                `아이디 또는 비밀번호 인증에 실패했습니다. (${getFirebaseErrorCode(error)})`
            );
        }
        const accountEmail = credential.user.email;
        if (!accountEmail) {
            await signOut(auth);
            throw new Error('The administrator account does not have an email address.');
        }

        const state = createState();
        const callbackUrl = new URL('/login', window.location.origin);
        callbackUrl.searchParams.set(EMAIL_LINK_STATE_PARAM, state);

        const pending: PendingEmailLink = {
            email: accountEmail,
            uid: credential.user.uid,
            state,
            requestedAt: Date.now(),
        };
        localStorage.setItem(EMAIL_LINK_PENDING_KEY, JSON.stringify(pending));
        setPendingEmail(accountEmail);

        try {
            await sendSignInLinkToEmail(auth, accountEmail, {
                url: callbackUrl.toString(),
                handleCodeInApp: true,
            });
        } catch (error) {
            localStorage.removeItem(EMAIL_LINK_PENDING_KEY);
            setPendingEmail(null);
            await signOut(auth);
            throw new Error(
                `비밀번호 인증은 완료됐지만 인증 링크 발송에 실패했습니다. (${getFirebaseErrorCode(error)})`
            );
        }

        return accountEmail;
    }, []);

    const completeEmailLinkSignIn = useCallback(async (link: string) => {
        const currentUser = auth.currentUser;
        const pending = readPendingEmailLink();
        const linkUrl = new URL(link);
        const state = linkUrl.searchParams.get(EMAIL_LINK_STATE_PARAM);
        const apiKey = linkUrl.searchParams.get('apiKey');
        const oobCode = linkUrl.searchParams.get('oobCode');

        if (!currentUser || !pending) {
            throw new Error('인증 요청 정보가 없습니다. 비밀번호 로그인부터 다시 진행해주세요.');
        }
        if (pending.uid !== currentUser.uid || pending.state !== state) {
            throw new Error('인증 링크가 현재 로그인 요청과 일치하지 않습니다.');
        }
        if (!apiKey || !oobCode) {
            throw new Error('유효하지 않은 이메일 인증 링크입니다.');
        }

        const completeEmailLink = httpsCallable<
            CompleteEmailLinkRequest,
            CompleteEmailLinkResponse
        >(functions, 'completeAdminEmailLink');
        const result = await completeEmailLink({
            apiKey,
            idToken: await currentUser.getIdToken(),
            oobCode,
        });
        if (!result.data.verified) {
            throw new Error('이메일 링크 인증을 완료하지 못했습니다.');
        }

        await currentUser.getIdToken(true);
        const verification = await getEmailLinkVerification(currentUser);
        if (!verification.verified) {
            throw new Error('이메일 인증 세션을 확인하지 못했습니다.');
        }

        localStorage.removeItem(EMAIL_LINK_PENDING_KEY);
        setPendingEmail(null);
        setEmailLinkVerified(true);
        setVerificationExpiresAt(verification.expiresAt);
    }, []);

    const logout = useCallback(async () => {
        localStorage.removeItem(EMAIL_LINK_PENDING_KEY);
        setEmailLinkVerified(false);
        setPendingEmail(null);
        setVerificationExpiresAt(null);
        await signOut(auth);
        setUser(null);
    }, []);

    const value = {
        user,
        loading,
        emailLinkVerified,
        pendingEmail,
        signIn,
        completeEmailLinkSignIn,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
